import keys from 'constants/keys'
import { RootState } from 'constants/interfaces'
import { getChat, getPost, setNotificationChatModal, setNotificationPostModal } from 'redux/actions/feed'
import { setTaskOverviewModal } from 'redux/actions/tasks_v2'
import {
  createServiceNotificationResolver,
  INotificationEventHandler,
} from 'services/notifications/createServiceNotificationResolver'
import { INotification, IResolvedDestination } from 'services/notifications/types'

const CONTENT_UNAVAILABLE_MESSAGE = 'Content is no longer available'

const getString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}

const getPostNotificationType = (
  notification: INotification,
  objectRef: Record<string, any> | null,
  data: Record<string, any>
) => getString(notification.post_type ?? objectRef?.post_type ?? data.post_type)?.toLowerCase()

const isChatPostNotification = (
  notification: INotification,
  objectRef: Record<string, any> | null,
  data: Record<string, any>
) => getPostNotificationType(notification, objectRef, data) === 'chat'

const getExistingChatFromStore = (dispatch: (action: any) => any, postId: string) =>
  dispatch((_dispatch: unknown, getState: () => RootState) =>
    getState().feed.chats.find((chat) => chat.post_id === postId)
  )

const getPostRef = (notification: INotification) => {
  const objectRef = notification.object_ref ?? notification.data?.object_ref ?? null
  const data = notification.data ?? {}
  const postId = getString(
    objectRef?.post_id ??
      objectRef?.chat_id ??
      objectRef?.object_id ??
      objectRef?.id ??
      data?.post_id ??
      data?.chat_id ??
      data?.object_id
  )
  const contextId = getString(objectRef?.context_id ?? objectRef?.group_id ?? data?.context_id ?? data?.group_id)
  const isChat = isChatPostNotification(notification, objectRef, data)

  return { postId, contextId, isChat }
}

const POST_EVENTS = ['post_created', 'post_ping', 'post_activity_counter']

const openPostEvent: INotificationEventHandler = {
  eventTypes: POST_EVENTS,
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const { postId, contextId, isChat } = getPostRef(notification)
    if (!postId) return

    dispatch(setTaskOverviewModal(null))

    if (isChat) {
      dispatch(setNotificationPostModal(null))
      dispatch(setNotificationChatModal({ id: postId }))
      const chat =
        getExistingChatFromStore(dispatch, postId) ??
        (contextId ? await dispatch(getChat({ chat_id: postId, contextId })) : null)
      if (!chat) {
        dispatch(setNotificationChatModal({ id: postId, error: CONTENT_UNAVAILABLE_MESSAGE }))
      }
      return
    }

    dispatch(setNotificationChatModal(null))
    dispatch(setNotificationPostModal({ id: postId }))
    const post = contextId ? await dispatch(getPost({ postId, contextId })) : null
    if (!post) {
      dispatch(setNotificationPostModal({ id: postId, error: CONTENT_UNAVAILABLE_MESSAGE }))
    }
  },
  async resolve(notification: INotification): Promise<IResolvedDestination> {
    if (notification.target?.route) {
      return { path: notification.target.route, replace: true }
    }

    // onBeforeNavigate already opens the post/chat modal directly whenever it has a postId to work
    // with (even a fetch failure still surfaces as an in-modal "unavailable" state, not a blank
    // page) — don't also navigate to the News Feed in that case. Only do that when there's no
    // postId at all, i.e. we couldn't even attempt to open anything.
    const { postId } = getPostRef(notification)
    if (postId) return { handled: true }

    return { path: keys.ROUTE_NAMES.NEWS_FEED, replace: true }
  },
}

const fallbackEvent: INotificationEventHandler = {
  eventTypes: [],
  async resolve(): Promise<IResolvedDestination> {
    return { path: keys.ROUTE_NAMES.NEWS_FEED, replace: true }
  },
}

export const postsResolver = createServiceNotificationResolver({
  id: 'posts',
  service: 'posts',
  events: [openPostEvent],
  fallback: fallbackEvent,
})
